import {
  interpolateCividis,
  interpolateYlOrRd,
  interpolateViridis,
  scaleLinear,
  schemeCategory10,
  schemeAccent,
  schemeDark2,
  schemePaired,
  schemeSet1,
  schemeSet2,
  schemeSet3,
  schemeTableau10,
  interpolateSpectral,
  interpolateCubehelixDefault,
  interpolateInferno,
  interpolateCool,
  interpolateWarm,
  interpolateTurbo,
  interpolateRgb,
} from "d3";
import {
  pathogenicVeryStrongColor,
  pathogenicModerateColor,
  benignVeryStrongColor,
  benignModerateColor,
  uncertainColor,
} from "../../../common/helpers/colors";
import {
  blue,
  blueGray,
  white,
  black,
  emerald,
  rose,
  fuchsia,
  indigo,
  amber,
  cyan,
} from "../../../../../tailwind.colors";
import Color from "../models/Color";

const viridis = interpolateViridis;
const ylrd = interpolateYlOrRd;
const cividis = interpolateCividis;
const cubeHelix = interpolateCubehelixDefault;
const inferno = interpolateInferno;
const spectral = interpolateSpectral;
const cool = interpolateCool;
const warm = interpolateWarm;
const turbo = interpolateTurbo;

const l = (s) => scaleLinear().domain([0, s - 1]);
const c = (s) => (x) => s[x % s.length];

const blueGrayScale = (s) => {
  return scaleLinear().domain([0, s]).range([blueGray[50], blueGray[900]]);
};

const blackWhiteScale = (s) => {
  return scaleLinear().domain([0, s]).range([black, white]);
};

const redGreenScale = (s, invert = false) => {
  const domain = invert
    ? [0, s * 0.25, s * 0.5, s * 0.75, s].reverse()
    : [0, s * 0.25, s * 0.5, s * 0.75, s];
  return scaleLinear()
    .domain(domain)
    .range([
      pathogenicVeryStrongColor,
      pathogenicModerateColor,
      uncertainColor,
      benignModerateColor,
      benignVeryStrongColor,
    ])
    .interpolate(interpolateRgb.gamma(2.2));
};

const blueRedScale = (s, invert = false) => {
  return scaleLinear()
    .domain(invert ? [s, 0] : [0, s])
    .range(["#5497f7", "red"])
    .interpolate(interpolateRgb.gamma(2.2));
};

const tailwind = [
  blueGray[400],
  emerald[400],
  blue[400],
  indigo[500],
  fuchsia[400],
  rose[400],
  amber[400],
  cyan[400],
];

export const VIRIDIS_COOL = "VIRIDIS_COOL";
export const YELLOW_RED = "YELLOW_RED";
export const RED_GREEN = "RED_GREEN";
export const GREEN_RED = "GREEN_RED";
export const BLUE_RED = "BLUE_RED";
export const RED_BLUE = "RED_BLUE";
export const COOL = "COOL";
export const WARM = "WARM";
export const CIVIDIS = "CIVIDIS";
export const GRAY_TONE = "GRAY_TONE";
export const BLUE_GRAY = "BLUE_GRAY";
export const BLACK_WHITE = "BLACK_WHITE";
export const SPECTRAL = "SPECTRAL";
export const SCHEME_10 = "SCHEME_10";
export const SCHEME_ACCENT = "SCHEME_ACCENT";
export const SCHEME_DARK = "SCHEME_DARK";
export const SCHEME_PAIRED = "SCHEME_PAIRED";
export const SCHEME_SET1 = "SCHEME_SET1";
export const SCHEME_SET2 = "SCHEME_SET2";
export const SCHEME_SET3 = "SCHEME_SET3";
export const TABLEAU = "TABLEAU";
export const CUBE_HELIX = "CUBE_HELIX";
export const INFERNO = "INFERNO";
export const TURBO = "TURBO";
export const TAILWIND = "TAILWIND";

export const colorScales = {
  [TURBO]: (space) => {
    const s = l(space);
    return (x) => turbo(s(x) * 0.8 + 0.1);
  },
  [VIRIDIS_COOL]: (space) => {
    const s = l(space);
    return (x) => viridis(s(x) * 0.8);
  },
  [YELLOW_RED]: (space) => {
    const s = l(space);
    return (x) => ylrd(s(x) * 0.5 + 0.5);
  },
  [GRAY_TONE]: (space) => {
    const s = l(space);
    return (x) => cividis(s(x) * 0.4 + 0.2);
  },
  [COOL]: (space) => {
    const s = l(space);
    return (x) => cool(s(x) * 0.6 + 0.1);
  },
  [WARM]: (space) => {
    const s = l(space);
    return (x) => warm(s(x) * 0.6 + 0.2);
  },
  [CUBE_HELIX]: (space) => {
    const s = l(space);
    return (x) => cubeHelix(s(x) * 0.4 + 0.2);
  },
  [INFERNO]: (space) => {
    const s = l(space);
    return (x) => inferno(s(x) * 0.7 + 0.15);
  },
  [SPECTRAL]: (space) => {
    const s = l(space);
    return (x) => spectral(s(x));
  },
  [CIVIDIS]: (space) => {
    const s = l(space);
    return (x) => cividis(s(x) * 0.7 + 0.15);
  },
  [BLUE_GRAY]: (s) => {
    return blueGrayScale(s - 1);
  },
  [BLACK_WHITE]: (s) => {
    return blackWhiteScale(s - 1);
  },
  [RED_GREEN]: (s) => {
    return redGreenScale(s - 1);
  },
  [BLUE_RED]: (s) => {
    return blueRedScale(s - 1);
  },
  [GREEN_RED]: (s) => {
    return redGreenScale(s - 1, true);
  },
  [RED_BLUE]: (s) => {
    return blueRedScale(s - 1, true);
  },
  [TAILWIND]: () => c(tailwind),
  [SCHEME_10]: () => c(schemeCategory10),
  [SCHEME_ACCENT]: () => c(schemeAccent),
  [SCHEME_DARK]: () => c(schemeDark2),
  [SCHEME_PAIRED]: () => c(schemePaired),
  [SCHEME_SET1]: () => c(schemeSet1),
  [SCHEME_SET2]: () => c(schemeSet2),
  [SCHEME_SET3]: () => c(schemeSet3),
  [TABLEAU]: () => c(schemeTableau10),
};

export const labels = {
  [VIRIDIS_COOL]: "Viridis",
  [TURBO]: "Turbo",
  [COOL]: "Cool Rainbow",
  [WARM]: "Warm Rainbow",
  [CUBE_HELIX]: "Cube Helix",
  [YELLOW_RED]: "Orange / Red",
  [RED_GREEN]: "Red / Green",
  [GREEN_RED]: "Green / Red",
  [BLUE_RED]: "Blue / Red",
  [RED_BLUE]: "Red / Blue",
  [CIVIDIS]: "Cividis",
  [GRAY_TONE]: "Gray Tone",
  [INFERNO]: "Inferno",
  [SPECTRAL]: "Spectral",
  [TABLEAU]: "Tableau",
  [TAILWIND]: "Tailwind",
  [SCHEME_10]: "Set 1",
  [SCHEME_ACCENT]: "Set 2",
  [SCHEME_DARK]: "Set 3",
  [SCHEME_PAIRED]: "Set 4",
  [SCHEME_SET1]: "Set 5",
  [SCHEME_SET2]: "Set 6",
  [SCHEME_SET3]: "Set 7",
};

// selection color
export const gray100 = new Color(blueGray[100]);
export const gray800 = new Color(blueGray[800]);

export function getSelectionColorMap(isDark) {
  return isDark ? gray100.vec : gray800.vec;
}
